import { useMemo, useState } from 'react';

import { useAppContext } from '@/components/utilities/AppContext';
import VisibleLoading from '@/components/utilities/VisibleLoading';
import { getGlossaryTermUrl } from '@/utils/glossary-routes';
import { useGlossaryLabels } from '@/utils/glossary-labels';

import GlossarySearchBar from './glossary/GlossarySearchBar';
import GlossaryLetterFilter from './glossary/GlossaryLetterFilter';
import GlossaryTermCard from './glossary/GlossaryTermCard';

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getFirstLetter = (title) => {
  const normalized = normalize(title);
  return normalized.charAt(0).toUpperCase();
};

const pluralizeTerms = (count, labels) => {
  if (count === 1) return labels.resultsSingular;
  if (count < 5) return labels.resultsFew;
  return labels.resultsMany;
};

const GlossaryListBlock = ({ title, description, eyebrow }) => {
  const { glossaryTerms, locale } = useAppContext();
  const labels = useGlossaryLabels();
  const terms = useMemo(() => glossaryTerms || [], [glossaryTerms]);

  const [query, setQuery] = useState('');
  const [letter, setLetter] = useState('all');

  const availableLetters = useMemo(() => {
    const set = new Set();
    terms.forEach((term) => set.add(getFirstLetter(term.title)));
    return set;
  }, [terms]);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());

    return terms.filter((term) => {
      const matchesQuery = !q || normalize(term.title).includes(q) || normalize(term.shortDescription).includes(q);

      if (!matchesQuery) return false;

      if (letter === 'all' || q) return true;

      return getFirstLetter(term.title) === letter;
    });
  }, [terms, query, letter]);

  const handleQueryChange = (nextQuery) => {
    setQuery(nextQuery);
    if (nextQuery && letter !== 'all') {
      setLetter('all');
    }
  };

  return (
    <section className="bg-white pb-20">
      <div className="mx-auto flex w-[90vw] flex-col items-center gap-5 pt-14 pb-[72px] text-center xl:w-(--container-width) xl:pt-20">
        {eyebrow && (
          <VisibleLoading delay={75}>
            {({ ref, className }) => (
              <span
                ref={ref}
                className={`text-brand-500 text-xs font-semibold tracking-[0.08em] uppercase ${className || ''}`.trim()}
              >
                {eyebrow}
              </span>
            )}
          </VisibleLoading>
        )}

        {title && (
          <VisibleLoading delay={100}>
            {({ ref, className }) => (
              <h1 ref={ref} className={`mx-auto max-w-[720px] ${className || ''}`.trim()}>
                {title}
              </h1>
            )}
          </VisibleLoading>
        )}

        {description && (
          <VisibleLoading delay={150}>
            {({ ref, className }) => (
              <p
                ref={ref}
                className={`text-grey-600 mx-auto max-w-[520px] text-lg leading-relaxed ${className || ''}`.trim()}
              >
                {description}
              </p>
            )}
          </VisibleLoading>
        )}

        <div className="mt-5 w-full max-w-[540px]">
          <GlossarySearchBar
            value={query}
            onChange={handleQueryChange}
            placeholder={labels.searchPlaceholder}
            ariaLabel={labels.searchAriaLabel}
          />
        </div>
      </div>

      <div className="border-grey-200 sticky top-0 z-40 border-b bg-white/95 pt-7 backdrop-blur-md">
        <div className="mx-auto w-[90vw] pb-5 xl:w-(--container-width)">
          <GlossaryLetterFilter
            activeLetter={letter}
            availableLetters={availableLetters}
            onChange={setLetter}
            allLabel={labels.allLabel}
            filterLabel={labels.filterLabel}
          />
        </div>
      </div>

      <div className="mx-auto w-[90vw] xl:w-(--container-width)">
        <p className="text-grey-500 pt-5 pb-2 text-sm" aria-live="polite">
          {filtered.length > 0 ? `${filtered.length} ${pluralizeTerms(filtered.length, labels)}` : ''}
        </p>

        {filtered.length === 0 ? (
          <div className="border-grey-200 mt-6 rounded-2xl border bg-white px-6 py-20 text-center">
            <p className="text-grey-900 mb-2 text-lg font-semibold">{labels.emptyTitle}</p>
            <p className="text-grey-500 text-sm">{labels.emptyDescription}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((term) => (
              <GlossaryTermCard
                key={term.id}
                term={term}
                href={getGlossaryTermUrl(term.slug, locale)}
                readLabel={labels.termCardReadLabel}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GlossaryListBlock;
